import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import './signin.css';
import BASE_URL from '../../config';

const Login = ({ setToken }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guards against a double-click/double-tap firing two concurrent
    // POST /api/login/ requests -- without this, two near-simultaneous
    // logins that both need the 7-day re-verification step used to each
    // mint their own OTP, invalidating one another (see the matching fix
    // in mavis_backend's LoginAPIView, which now also reuses a still-valid
    // OTP as a second layer of defense).
    if (loading) return;
    setError('');

    const credentials = {
      username,
      password,
    };

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/login/`, credentials);
      const { access } = response.data;

      localStorage.setItem('jwt_token', access);
      setToken(access);

      const userRes = await axios.get(`${BASE_URL}/api/user-profile/`, {
        headers: {
          Authorization: `Bearer ${access}`,
        },
      });

      const isAdmin = userRes.data.is_admin;

      if (isAdmin) {
        navigate("/user-dashboard/admin-dashboard");
      } else {
        navigate("/user-dashboard/dashboard");
      }

    } catch (error) {
      console.error('Login failed', error);

      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;

        navigate('/verify-email', {
          state: {
            email: username,
            isPeriodicVerification: errorData.requires_verification === true
          }
        });
        return;
      }

      if (error.response && error.response.data) {
        setError(error.response.data.message || error.response.data.detail || 'Invalid credentials. Please check your email and password.');
      } else {
        setError('Invalid credentials. Please check your email and password.');
      }
    } finally {
      setLoading(false);
    }

  };

  return (
    <main className="login-page-wrapper">
      <div className="login-container" role="region" aria-labelledby="login-title">
        <h1 id="login-title" className="login-title">Login</h1>

        {error && (
          <div
            ref={errorRef}
            className="login-error"
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <div className="form-group">
            <label htmlFor="login-email" className="form-label">Email</label>
            <input
              id="login-email"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="form-input"
              required
              aria-required="true"
              autoComplete="email"
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password" className="form-label">Password</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              required
              aria-required="true"
              autoComplete="current-password"
              placeholder="Enter your password"
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Logging in...' : 'Login'}</button>
        </form>
        <nav className="login-links" aria-label="Account actions">
          <div className="register-link">
            <p>No account? <Link to="/validate">Register</Link></p>
          </div>
          <div className="forgot-password-link">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
        </nav>
      </div>
    </main>
  );
};

export default Login;
